// Price Card Component - Newspaper Classified Style
import { t, Language } from '../translations';

export interface PriceCardData {
  key: string;
  label: string;
  labelMm?: string; // Optional for compatibility
  value: number | null;
  unit: string;
  source?: string;
  delta: {
    value: number | null;
    change: number | null;
    percentChange: number | null;
    trend: 'up' | 'down' | 'stable' | 'unknown';
  };
  sparklineData?: Array<number | null>;
  lang?: Language; // Add language prop
  isFavorite?: boolean; // Add favorite status
}

/**
 * Render a newspaper-style price listing
 */
export function renderPriceCard(data: PriceCardData): string {
  const { key, label, labelMm, value, unit, source, delta, lang = 'en', isFavorite = false } = data;

  const displayLabel = (lang === 'mm' && labelMm) ? labelMm : label;

  // Format Value: Use Burmese digits if MM? (Optional, kept standard for now)
  const displayValue = value !== null
    ? value.toLocaleString('en-US', { maximumFractionDigits: 0 })
    : '—';

  // Trend indicator
  let trendHTML = `<span class="trend-badge trend-stable">• ${t('trendStable', lang)}</span>`;
  if (delta.trend !== 'unknown' && delta.percentChange !== null) {
    const isUp = delta.trend === 'up';
    const colorClass = isUp ? 'trend-up' : (delta.trend === 'down' ? 'trend-down' : 'trend-stable');
    const arrow = isUp ? '▲' : (delta.trend === 'down' ? '▼' : '•');
    const percent = Math.abs(delta.percentChange).toFixed(1);

    trendHTML = `<span class="trend-badge ${colorClass}">${arrow} ${percent}%</span>`;
  }

  // Sparkline container ID
  const sparklineId = `sparkline-${key}`;

  // Add to Ledger Button text
  const addBtnText = t('addToCart', lang);
  const addedBtnText = t('addedToCart', lang);

  const addBtnHTML = `
    <button class="add-btn" 
       data-key="${key}" 
       data-label="${displayLabel}" 
       data-unit="${unit}" 
       data-price="${value !== null ? value : 0}"
       data-text-add="${addBtnText}"
       data-text-added="${addedBtnText}">
       ${addBtnText}
    </button>
  `;

  return `
    <article class="price-card" data-key="${key}">
      <div class="card-top">
        <div>
          <h2 class="card-title">${displayLabel}</h2>
        </div>
        <div style="display: flex; align-items: center; gap: 0.5rem;">
          ${source ? `<span class="card-source">${source}</span>` : ''}
          <button class="fav-btn ${isFavorite ? 'active' : ''}" data-fav-key="${key}" title="${isFavorite ? 'Remove from favorites' : 'Add to favorites'}" aria-label="${isFavorite ? 'Remove from favorites' : 'Add to favorites'}">
            ${isFavorite ? '★' : '☆'}
          </button>
        </div>
      </div>

      <div class="card-main">
        <span class="card-price">${displayValue}</span>
        <span class="card-unit">${unit}</span>
        ${trendHTML}
      </div>

      <div class="card-actions">
         <div id="${sparklineId}" class="sparkline-container"></div>
         ${value !== null ? addBtnHTML : ''}
      </div>
    </article>
  `;
}
